import { useState } from "react";
import { Box, Typography, Chip, Paper, IconButton, Select, MenuItem, Popover, Badge, Button } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { PersonRemove, FilterList } from "@mui/icons-material";
import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import type { DealConfigurationProps } from "./DealConfiguration.types";
import { useDealConfigurationViewModel, DEAL_PROPERTIES, type DealSortOption } from "./DealConfiguration.vm";
import { GrowerCard, EntityCardMenu, UnassignedGrowersPanel, EntitySearchInput } from "../shared";
import { PortalTooltip } from "../../../../../../helpers";

function DealPropertyChip({
	label,
	value,
	colorMap,
	propertyName,
}: {
	label: string;
	value: string;
	colorMap: Record<string, string>;
	propertyName: string;
}) {
	const bgcolor = colorMap[`${propertyName}:${value}`] || "transparent";
	return (
		<Chip
			label={
				<>
					<span style={{ fontWeight: 700 }}>{label}:</span> {value}
				</>
			}
			size="small"
			sx={{
				bgcolor,
				color: "text.primary",
				fontWeight: 400,
				fontSize: "0.7rem",
				height: 24,
			}}
		/>
	);
}

function DealsHeader({
	vm,
	totalCount,
	shownCount,
}: {
	vm: ReturnType<typeof useDealConfigurationViewModel>;
	totalCount: number;
	shownCount: number;
}) {
	const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
	const isFiltering = vm.activeDealFilterCount > 0 || vm.searchText.trim().length > 0;

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
			<Typography variant="subtitle2" sx={{ whiteSpace: "nowrap" }}>
				Configurations {isFiltering ? `(${shownCount} / ${totalCount})` : `(${totalCount})`}
			</Typography>
			<EntitySearchInput
				value={vm.searchText}
				onChange={vm.setSearchText}
				placeholder="Search configs..."
				sx={{ ml: "auto" }}
			/>
			<Select
				size="small"
				value={vm.dealSortOption}
				onChange={(e: SelectChangeEvent) => vm.setDealSortOption(e.target.value as DealSortOption)}
				sx={{
					fontSize: "0.75rem",
					height: 28,
					minWidth: 0,
					"& .MuiSelect-select": { py: 0.25, pr: "24px !important", pl: 1 },
				}}
			>
				<MenuItem value="default" sx={{ fontSize: "0.8rem" }}>
					Default
				</MenuItem>
				<MenuItem value="density-asc" sx={{ fontSize: "0.8rem" }}>
					Density ↑
				</MenuItem>
				<MenuItem value="density-desc" sx={{ fontSize: "0.8rem" }}>
					Density ↓
				</MenuItem>
				<MenuItem value="depth-asc" sx={{ fontSize: "0.8rem" }}>
					Depth ↑
				</MenuItem>
				<MenuItem value="depth-desc" sx={{ fontSize: "0.8rem" }}>
					Depth ↓
				</MenuItem>
				<MenuItem value="growers-desc" sx={{ fontSize: "0.8rem" }}>
					Growers ↑
				</MenuItem>
				<MenuItem value="growers-asc" sx={{ fontSize: "0.8rem" }}>
					Growers ↓
				</MenuItem>
			</Select>
			<PortalTooltip title="Filter configurations">
				<IconButton size="small" onClick={(e) => setFilterAnchorEl(e.currentTarget)}>
					<Badge badgeContent={vm.activeDealFilterCount} color="primary" overlap="circular">
						<FilterList fontSize="small" />
					</Badge>
				</IconButton>
			</PortalTooltip>
			<PortalTooltip title="Unassign all growers from all configurations">
				<IconButton size="small" onClick={vm.unassignAll}>
					<PersonRemove fontSize="small" />
				</IconButton>
			</PortalTooltip>
			<Popover
				open={Boolean(filterAnchorEl)}
				anchorEl={filterAnchorEl}
				onClose={() => setFilterAnchorEl(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
			>
				<Box sx={{ p: 1, maxHeight: 320, overflowY: "auto" }}>
					{DEAL_PROPERTIES.map((prop) => {
						const options = vm.dealFilterOptions[prop.name] || [];
						if (options.length <= 1) return null;
						const activeSet = vm.dealFilters[prop.name] || new Set<string>();
						return (
							<Box key={prop.name} sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5, flexWrap: "wrap" }}>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ fontWeight: 600, fontSize: "0.65rem", minWidth: 48 }}
								>
									{prop.label}:
								</Typography>
								{options.map((value) => {
									const isActive = activeSet.has(value);
									return (
										<Chip
											key={value}
											label={value}
											size="small"
											variant={isActive ? "filled" : "outlined"}
											color={isActive ? "primary" : "default"}
											onClick={() => {
												const next = new Set(activeSet);
												if (isActive) {
													next.delete(value);
												} else {
													next.add(value);
												}
												vm.setDealFilter(prop.name, next);
											}}
											sx={{ fontSize: "0.65rem", height: 22, cursor: "pointer" }}
										/>
									);
								})}
							</Box>
						);
					})}
					{isFiltering && (
						<Button
							size="small"
							onClick={() => {
								vm.clearDealFilters();
								setFilterAnchorEl(null);
							}}
							sx={{ mt: 1, width: "100%" }}
						>
							Clear All
						</Button>
					)}
				</Box>
			</Popover>
		</Box>
	);
}

export function DealConfiguration(props: DealConfigurationProps) {
	const vm = useDealConfigurationViewModel(props);

	return (
		<DragDropContext onBeforeCapture={vm.handleBeforeCapture} onDragEnd={vm.handleDragEnd}>
			<Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
				<Box sx={{ mb: 2 }}>
					<Typography variant="body2" color="text.secondary">
						The jobs have been grouped by grower.
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Drag-and-drop to assign their jobs to a sampling configuration.
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Each configuration&apos;s properties are highlighted where they differ.
					</Typography>
				</Box>
				<Box sx={{ display: "flex", gap: 2, flex: 1, minHeight: 400 }}>
					<UnassignedGrowersPanel
						unassignedGrowers={vm.unassignedGrowers}
						growerSortOption={vm.growerSortOption}
						setGrowerSortOption={vm.setGrowerSortOption}
						selectedGrowers={vm.selectedGrowers}
						clearSelection={vm.clearSelection}
						handleGrowerClick={vm.handleGrowerClick}
						draggingSelectedCount={vm.draggingSelectedCount}
						UNASSIGNED_DROPPABLE_ID={vm.UNASSIGNED_DROPPABLE_ID}
					/>

					{/* Right: Deal cards grid */}
					<Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
						<DealsHeader vm={vm} totalCount={vm.totalDealCount} shownCount={vm.filteredSortedDeals.length} />
						<Box sx={{ flex: 1, overflowY: "auto" }}>
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
									gap: 1.5,
								}}
							>
								{vm.filteredSortedDeals.map((deal) => {
									const assignedGrowers = vm.growersByEntity.get(deal.id) || [];
									return (
										<Droppable key={deal.id} droppableId={deal.id}>
											{(provided, snapshot) => (
												<Paper
													ref={provided.innerRef}
													{...provided.droppableProps}
													variant="outlined"
													sx={{
														p: 1.5,
														bgcolor: snapshot.isDraggingOver ? "primary.50" : "background.paper",
														borderColor: snapshot.isDraggingOver ? "primary.main" : "divider",
														borderWidth: snapshot.isDraggingOver ? 2 : 1,
														transition: "border-color 0.15s, background-color 0.15s",
													}}
												>
													{/* Deal properties — 2-column grid */}
													<Box sx={{ mb: 1, display: "flex", alignItems: "flex-start" }}>
														<Box sx={{ flex: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
															<DealPropertyChip
																label="Season"
																value={deal.season}
																propertyName="season"
																colorMap={vm.propertyColorMap}
															/>
															<DealPropertyChip
																label="Type"
																value={deal.type}
																propertyName="type"
																colorMap={vm.propertyColorMap}
															/>
															<DealPropertyChip
																label="Density"
																value={`${deal.density} acre`}
																propertyName="density"
																colorMap={vm.propertyColorMap}
															/>
															<DealPropertyChip
																label="Depth"
																value={`${deal.depth} in`}
																propertyName="depth"
																colorMap={vm.propertyColorMap}
															/>
															<DealPropertyChip
																label="Package"
																value={deal.testPackage || "None"}
																propertyName="testPackage"
																colorMap={vm.propertyColorMap}
															/>
															<DealPropertyChip
																label="Micros"
																value={`${deal.frequencyOfMicros ?? 0}`}
																propertyName="frequencyOfMicros"
																colorMap={vm.propertyColorMap}
															/>
															{deal.labName && (
																<DealPropertyChip
																	label="Lab"
																	value={deal.labName}
																	propertyName="labName"
																	colorMap={vm.propertyColorMap}
																/>
															)}
															{deal.specialTag && (
																<DealPropertyChip
																	label="Tag"
																	value={deal.specialTag}
																	propertyName="specialTag"
																	colorMap={vm.propertyColorMap}
																/>
															)}
														</Box>
														<EntityCardMenu
															entityId={deal.id}
															onAssignAll={vm.assignAllToEntity}
															onAssignUnassigned={vm.assignUnassignedToEntity}
															onUnassignAll={vm.unassignAllFromEntity}
															selectedCount={vm.selectedGrowers.size}
															onAssignSelected={vm.assignSelectedToEntity}
														/>
													</Box>

													{/* Assigned growers */}
													<Box sx={{ minHeight: 36 }}>
														{assignedGrowers.length === 0 && !snapshot.isDraggingOver && (
															<Typography variant="caption" color="text.disabled" sx={{ fontStyle: "italic" }}>
																Drop growers here
															</Typography>
														)}
														{assignedGrowers.map((grower, index) => (
															<GrowerCard
																key={grower.name}
																grower={grower}
																index={index}
																isSelected={vm.selectedGrowers.has(grower.name)}
																onToggleSelect={vm.handleGrowerClick}
																multiDragCount={vm.selectedGrowers.has(grower.name) ? vm.draggingSelectedCount : 0}
															/>
														))}
														{provided.placeholder}
													</Box>
												</Paper>
											)}
										</Droppable>
									);
								})}
							</Box>
							{vm.filteredSortedDeals.length === 0 && vm.totalDealCount > 0 && (
								<Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 4 }}>
									No configurations match the current {vm.searchText.trim() ? "search" : "filters"}.
								</Typography>
							)}
							{vm.totalDealCount === 0 && (
								<Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 4 }}>
									No deals available. Please create deals before proceeding.
								</Typography>
							)}
						</Box>
					</Box>
				</Box>
			</Box>
		</DragDropContext>
	);
}
